import axios from 'axios'

import {useUserStore} from '@/store/user'
import {Message} from "@arco-design/web-vue";
// 获取路由实例
import router from '@/router'; // 直接导入router实例

// 注意：在模块顶层不应该直接调用useUserStore()
// 应该在需要的时候再调用

/**
 * 清理对象中的无效值（空字符串、null、undefined等）
 * @param {Object} obj - 需要清理的对象
 * @param {boolean} deep - 是否深度清理嵌套对象，默认为true
 * @returns {Object} - 清理后的对象
 */
const cleanEmptyValues = (obj, deep = true) => {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }
  
  const cleaned = {};
  
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];
      
      // 检查是否为无效值
      if (value === null || 
          value === undefined || 
          value === '' || 
          (typeof value === 'string' && value.trim() === '')) {
        // 跳过无效值，不添加到清理后的对象中
        continue;
      }
      
      // 如果是数组，递归清理数组中的对象
      if (Array.isArray(value) && deep) {
        const cleanedArray = value.map(item => {
          if (typeof item === 'object' && item !== null) {
            return cleanEmptyValues(item, deep);
          }
          return item;
        }).filter(item => {
          // 过滤掉数组中的无效值
          return item !== null && 
                 item !== undefined && 
                 item !== '' && 
                 !(typeof item === 'string' && item.trim() === '');
        });
        
        if (cleanedArray.length > 0) {
          cleaned[key] = cleanedArray;
        }
      }
      // 如果是对象，递归清理
      else if (typeof value === 'object' && deep) {
        const cleanedObj = cleanEmptyValues(value, deep);
        // 只有当清理后的对象不为空时才添加
        if (Object.keys(cleanedObj).length > 0) {
          cleaned[key] = cleanedObj;
        }
      }
      // 其他有效值直接添加
      else {
        cleaned[key] = value;
      }
    }
  }
  
  return cleaned;
};

const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_URL,
  timeout: 10000
})

// 请求拦截器
service.interceptors.request.use(
  config => {
    // 在这里添加token等请求头配置
    const userStore = useUserStore(); // 在拦截器中调用
    if (userStore.getToken) {
        config.headers["Authorization"] = "Bearer " + userStore.getToken;
    }
    
    // 清理请求参数中的无效值
    if (config.params) {
      config.params = cleanEmptyValues(config.params);
    }
    
    // 清理请求体中的无效值（POST、PUT等请求）
    // ！！！特别注意如果请求体是FormData对象，则不需要清理无效值，因为如果是文件上传的话会破坏结构导致后端接收不到文件对象
    if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
      config.data = cleanEmptyValues(config.data);
    }
    
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    // 根据业务状态码处理响应
    return response.data
  },
  async error => { // 添加async关键字
      if (error.response) {
          const { status, data } = error.response;

          // 处理401未授权错误
          if (status === 401) {
              // 提示错误信息，使用你项目中的UI组件
              Message.error(data.message || '登录已过期，请重新登录');

              // 清除本地存储的token等信息（如果有）
              const userStore = useUserStore(); // 在需要的时候调用
              userStore.clearToken()
              userStore.clearUserInfo()

              // 跳转到登录页面，并携带重定向地址
              // 使用await确保路由跳转完成
              if(router.currentRoute.value.path !== '/login'){
                  await router.push({
                      path: '/login',
                      query: {
                          redirect: router.currentRoute.value.fullPath
                      }
                  });
              }
          }
      }
    // 统一错误处理
    return Promise.reject(error)
  }
)

export default service